/*
 * -----------------------------------------------------------------------
 *  <copyright file="date-picker/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { DatePicker as KendoDatePicker } from '@progress/kendo-react-dateinputs';
import cn from 'clsx';
import moment from 'moment';
import React, { useRef, useEffect } from 'react';

import { useBoolean } from 'hooks';

import './styles.scss';
import { NullableComponentRef, setAttribute } from '@utils/automation-test-helpers';

interface IDatePicker {
    label: string;
    name?: string;
    // labelWidth?: number;
    selectedDate: string | Date | null;
    handleDateChange: (value: any) => any;
    customId: string;
    // placeholder?: string;
    extraClassName?: string;
    setValid?: (isValid: boolean) => void;
    disabled?: boolean;
    error?: string;
    min?: Date | null;
    'data-testid'?: string;
}

const DatePicker = ({
    extraClassName,
    label,
    name,
    selectedDate,
    handleDateChange,
    customId,
    setValid,
    disabled,
    error,
    min,
    'data-testid': dataTestId,
}: IDatePicker) => {
    const isFocus = useBoolean(false);
    const picker = useRef<NullableComponentRef>(null);

    useEffect(() => {
        if (setValid && picker.current?.validity) {
            setValid(picker.current.validity.valid);
        }
    }, [picker]);

    const minYear = 1000;

    const classNamesLabel = isFocus.value ? 'on-focus' : '';

    const value = selectedDate ? new Date(selectedDate) : null;

    if (value) {
        value.setFullYear(moment(selectedDate, 'YYYY-M-D').year());
        if (typeof selectedDate === 'string') value.setMinutes(value.getMinutes() + value.getTimezoneOffset());
    }

    useEffect(() => setAttribute(picker, 'data-testid', dataTestId ?? customId ?? 'date-picker'));

    return (
        <>
            <label
                id={`outlined-label-${customId}`}
                className={cn(
                    'custom-label',
                    classNamesLabel,
                    { error },
                    { invalid: picker?.current?.validity?.valid === false },
                    { disabled },
                )}
                htmlFor={customId}
            >
                {label}
            </label>
            <KendoDatePicker
                className={cn(
                    'custom-date-picker',
                    classNamesLabel,
                    extraClassName,
                    { 'red-border': error },
                    { default: !value },
                )}
                name={name}
                id={customId}
                onFocus={() => {
                    isFocus.setTrue();
                }}
                onBlur={(event: any) => {
                    isFocus.setFalse();
                    if (picker.current && picker.current.show && !event.target.className.match('k-input'))
                        picker.current.setShow(false);
                }}
                ref={(instance: any) => {
                    (window as any)[customId || `date-picker-${label}`] = instance;
                    picker.current = instance;
                }}
                defaultValue={null}
                format="dd-MMM-yyyy"
                formatPlaceholder={{
                    day: 'dd',
                    month: 'Mmm',
                    year: 'yyyy',
                }}
                min={min ?? new Date(1000, 1, 1)}
                value={value}
                onChange={(event: any) => {
                    const year = moment(event.value).year();
                    if (year.toString().length <= 1 && year === 0) {
                        event.value = moment(event.value).set('year', minYear);
                    }

                    handleDateChange(event);
                }}
                title={label}
                disabled={disabled}
            />
            {error && <p className="request-date-error">{error}</p>}
        </>
    );
};

export default DatePicker;
